import { initSentry } from "@client/lib/sentry";
import { TriggerProvider } from "@client/lib/trigger-provider";
import { queryClient, trpc, trpcClient } from "@client/lib/trpc-client";
import { QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import ReactDOM from "react-dom/client";
import { routeTree } from "./routeTree.gen";

// Initialize Sentry as early as possible
initSentry().catch(console.error);

// Create router with wrapped query client
const router = createRouter({
	routeTree,
	defaultPreload: "intent",
	// React Query owns cache freshness; don't let the router short-circuit
	// loaders with its own preload cache.
	defaultPreloadStaleTime: 0,
	context: { trpc, queryClient },
	defaultOnCatch: (error) => console.error(error),
	Wrap: function WrapComponent({ children }) {
		return (
			// Reuse the singleton client (a new client per render would reset
			// in-flight request batching and diverge from route-loader prefetches).
			<trpc.Provider client={trpcClient} queryClient={queryClient}>
				<QueryClientProvider client={queryClient}>
					<TriggerProvider>{children}</TriggerProvider>
				</QueryClientProvider>
			</trpc.Provider>
		);
	},
});

declare global {
	interface Window {
		dataLayer?: Record<string, unknown>[];
		twq?: (...args: unknown[]) => void;
	}
}

// Push GTM page_view event on every route change
router.subscribe("onResolved", ({ toLocation }) => {
	window.dataLayer?.push({
		event: "page_view",
		page_path: toLocation.pathname,
		page_search: toLocation.search,
	});
});

// Register router for typesafety
declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}

// Render the app
const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Root element not found");

if (!rootElement.innerHTML) {
	const root = ReactDOM.createRoot(rootElement);
	root.render(<RouterProvider router={router} />);
}
